import { useState, useMemo, useCallback, useRef, useEffect } from 'react';

import {
  Box,
  Input,
  Button,
  Checkbox,
  Flex,
  Spinner,
  HStack,
  Tag,
  TagLabel,
  TagCloseButton,
  Text,
  Divider,
  Tooltip,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  List,
  ListItem,
  useOutsideClick,
  useColorModeValue,
  InputGroup,
  InputLeftElement,
} from '@chakra-ui/react';

import { ChevronDownIcon, SearchIcon } from '@chakra-ui/icons';

interface Item {
  id: number;
  name: string;
}

interface SelectedItemObject {
  id: number;
  canRemove: boolean;
}

type SelectedItemType = number | SelectedItemObject;

interface MultiSelectProps {
  selectedIds: SelectedItemType[];
  availableItems: Item[];
  isLoading: boolean;
  onChange: (selectedIds: number[]) => void;
  label?: string;
  maxDisplayedItems?: number;
  itemLabelSingular?: string;
  itemLabelPlural?: string;
  maxSelectedItems?: number;
  isDisabled?: boolean;
  colorScheme?: string;
  showTags?: boolean;
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  selectedIds,
  availableItems,
  isLoading,
  onChange,
  label = 'Select items',
  maxDisplayedItems = 100,
  itemLabelSingular = 'item',
  itemLabelPlural = 'items',
  maxSelectedItems,
  isDisabled = false,
  colorScheme = 'blue',
  showTags = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.300');
  const bgColor = useColorModeValue('white', 'gray.800');
  const hoverBgColor = useColorModeValue('gray.50', 'gray.700');

  // Internal state to track removability separately from IDs
  const [nonRemovableIds, setNonRemovableIds] = useState<number[]>(() => {
    return selectedIds
      .filter((item) => typeof item !== 'number' && !item.canRemove)
      .map((item) => (typeof item === 'number' ? item : item.id));
  });

  // Helper function to extract ID from either number or object
  const getItemId = useCallback((item: SelectedItemType): number => {
    return typeof item === 'number' ? item : item.id;
  }, []);

  // Get all selected IDs (regardless of object or number format)
  const selectedItemIds = useMemo(() => {
    return selectedIds.map((item) => getItemId(item));
  }, [selectedIds, getItemId]);

  // Track if we've reached the selection limit
  const isSelectionLimitReached = useMemo(
    () =>
      maxSelectedItems !== undefined && selectedIds.length >= maxSelectedItems,
    [selectedIds, maxSelectedItems],
  );

  // Effect to sync nonRemovableIds when selectedIds changes
  useEffect(() => {
    const newNonRemovableIds = selectedIds
      .filter((item) => typeof item !== 'number' && !item.canRemove)
      .map((item) => (typeof item === 'number' ? item : item.id));

    setNonRemovableIds(newNonRemovableIds);
  }, [selectedIds]);

  // Filter items based on search query
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) {
      return availableItems.slice(0, maxDisplayedItems);
    }

    const searchLower = searchQuery.toLowerCase();
    const filtered = availableItems.filter((item) =>
      item.name.toLowerCase().includes(searchLower),
    );
    return filtered.slice(0, maxDisplayedItems);
  }, [availableItems, searchQuery, maxDisplayedItems]);

  const hasMoreOptions = useMemo(() => {
    if (!searchQuery.trim()) {
      return availableItems.length > maxDisplayedItems;
    }
    const searchLower = searchQuery.toLowerCase();
    const allFilteredItems = availableItems.filter((item) =>
      item.name.toLowerCase().includes(searchLower),
    );
    return allFilteredItems.length > maxDisplayedItems;
  }, [availableItems, searchQuery, maxDisplayedItems]);

  // Close dropdown when clicking outside
  useOutsideClick({
    ref: ref,
    handler: () => {
      setIsOpen(false);
      setSearchQuery('');
    },
  });

  // Focus on input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current && !isDisabled) {
      inputRef.current.focus();
    }
  }, [isOpen, isDisabled]);

  // Handle item toggle (select/deselect)
  const handleItemToggle = useCallback(
    (itemId: number) => {
      if (isDisabled) return;

      const currentSelectedIds = [...selectedItemIds];
      const itemIndex = currentSelectedIds.indexOf(itemId);

      if (itemIndex !== -1) {
        // Check if we can remove this item
        if (!nonRemovableIds.includes(itemId)) {
          currentSelectedIds.splice(itemIndex, 1);
          onChange(currentSelectedIds);
        }
      } else {
        // Check if we can add more items
        if (
          maxSelectedItems === undefined ||
          currentSelectedIds.length < maxSelectedItems
        ) {
          currentSelectedIds.push(itemId);
          onChange(currentSelectedIds);
        }
      }

      // Keep dropdown open and maintain focus
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 0);
    },
    [selectedItemIds, onChange, maxSelectedItems, isDisabled, nonRemovableIds],
  );

  // Remove item from selection
  const removeItem = useCallback(
    (itemId: number) => {
      if (isDisabled || nonRemovableIds.includes(itemId)) return;
      onChange(selectedItemIds.filter((id) => id !== itemId));
    },
    [selectedItemIds, onChange, isDisabled, nonRemovableIds],
  );

  // Get item name by ID
  const getItemName = useCallback(
    (itemId: number) => {
      const item = availableItems.find((item) => item.id === itemId);
      return item ? item.name : `${itemLabelSingular} ${itemId}`;
    },
    [availableItems, itemLabelSingular],
  );

  // Generate label text for the button
  const buttonLabel = useMemo(() => {
    if (selectedIds.length === 0) {
      return label;
    }

    let text = `${selectedIds.length} ${
      selectedIds.length === 1 ? itemLabelSingular : itemLabelPlural
    } selected`;

    if (maxSelectedItems) {
      text += ` (max ${maxSelectedItems})`;
    }

    return text;
  }, [
    selectedIds.length,
    label,
    itemLabelSingular,
    itemLabelPlural,
    maxSelectedItems,
  ]);

  return (
    <Box>
      <Box ref={ref} position="relative" width="100%">
        <Popover
          isOpen={isOpen && !isDisabled}
          onClose={() => {
            setIsOpen(false);
            setSearchQuery('');
          }}
          placement="bottom"
          autoFocus={false}
          isLazy
          matchWidth={true}
          strategy="fixed"
        >
          <PopoverTrigger>
            <Button
              ref={triggerRef}
              onClick={() => setIsOpen(!isOpen)}
              rightIcon={<ChevronDownIcon />}
              width="100%"
              justifyContent="space-between"
              fontWeight="normal"
              variant="outline"
              isDisabled={isDisabled}
              isLoading={isLoading}
            >
              {buttonLabel}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            width="100%"
            border="1px solid"
            borderColor={borderColor}
            _focus={{
              outline: 'none',
              boxShadow: 'outline',
            }}
            boxShadow="md"
            position="absolute"
            zIndex={1400}
          >
            <Box
              position="sticky"
              top="0"
              zIndex={1}
              bg={bgColor}
              borderBottom="1px solid"
              borderColor={borderColor}
            >
              <InputGroup size="md">
                <InputLeftElement pointerEvents="none" pt={2}>
                  <SearchIcon color="gray.400" />
                </InputLeftElement>
                <Input
                  ref={inputRef}
                  placeholder={`Search ${itemLabelPlural}...`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                    }
                    e.stopPropagation();
                  }}
                  variant="unstyled"
                  px={3}
                  py={3}
                  pl={10}
                  borderRadius={0}
                  width="100%"
                  isDisabled={isDisabled}
                />
              </InputGroup>
            </Box>

            <PopoverBody p={0} maxH="300px" overflowY="auto">
              {isLoading ? (
                <Flex justify="center" py={4}>
                  <Spinner size="sm" />
                </Flex>
              ) : (
                <List spacing={0} width="100%">
                  {filteredItems.length === 0 ? (
                    <ListItem py={3} px={4} cursor="default" width="100%">
                      <Text color="gray.500" textAlign="center">
                        {searchQuery
                          ? `No matching ${itemLabelPlural} found`
                          : `No ${itemLabelPlural} available`}
                      </Text>
                    </ListItem>
                  ) : (
                    filteredItems.map((item) => {
                      const isItemSelected = selectedItemIds.includes(item.id);
                      const canRemove =
                        !isItemSelected || !nonRemovableIds.includes(item.id);
                      const isItemDisabled =
                        isDisabled ||
                        (isSelectionLimitReached && !isItemSelected) ||
                        (isItemSelected && !canRemove);

                      return (
                        <Tooltip
                          key={item.id}
                          isDisabled={!isItemDisabled || isDisabled}
                          label={
                            isDisabled
                              ? 'Selection is disabled'
                              : isItemSelected && !canRemove
                                ? 'This item cannot be removed'
                                : `Maximum of ${maxSelectedItems} ${itemLabelPlural} can be selected`
                          }
                          placement="top"
                        >
                          <ListItem
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (
                                !isItemDisabled ||
                                (isItemSelected && canRemove)
                              ) {
                                handleItemToggle(item.id);
                              }
                            }}
                            px={4}
                            py={2}
                            cursor={
                              isItemDisabled && !isItemSelected
                                ? 'not-allowed'
                                : 'pointer'
                            }
                            bg={bgColor}
                            _hover={{
                              bg:
                                isItemDisabled && !isItemSelected
                                  ? bgColor
                                  : hoverBgColor,
                            }}
                            opacity={
                              isItemDisabled && !isItemSelected ? 0.5 : 1
                            }
                            transition="background 0.2s"
                            display="flex"
                            alignItems="center"
                            gap={3}
                          >
                            <Checkbox
                              isChecked={isItemSelected}
                              colorScheme={colorScheme}
                              pointerEvents="none"
                              isDisabled={isItemDisabled && !isItemSelected}
                            />
                            <Text>{item.name}</Text>
                          </ListItem>
                        </Tooltip>
                      );
                    })
                  )}
                </List>
              )}

              {hasMoreOptions && (
                <Box borderColor={borderColor}>
                  <Divider />
                  <Text
                    fontSize="sm"
                    color="gray.500"
                    textAlign="center"
                    px={4}
                    py={2}
                  >
                    {availableItems.length - maxDisplayedItems} more items
                    available. Continue typing to narrow results.
                  </Text>
                </Box>
              )}
            </PopoverBody>
          </PopoverContent>
        </Popover>
      </Box>

      {showTags && selectedIds.length > 0 && (
        <HStack spacing={2} flexWrap="wrap" mt={2}>
          {selectedIds.map((item) => {
            const itemId = getItemId(item);
            const canRemove = !nonRemovableIds.includes(itemId);

            return (
              <Tag
                key={itemId}
                size="md"
                borderRadius="full"
                variant="solid"
                colorScheme={colorScheme}
                my={1}
                opacity={isDisabled ? 0.6 : 1}
              >
                <TagLabel>{getItemName(itemId)}</TagLabel>
                {!isDisabled && canRemove && (
                  <TagCloseButton onClick={() => removeItem(itemId)} />
                )}
              </Tag>
            );
          })}
        </HStack>
      )}
    </Box>
  );
};

export default MultiSelect;
